import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import toast from 'react-hot-toast';
import TableView from '../TableView';
import EditableTextCell from '../Presupuestos/EditableTextCell';
import CreateReporteModal from './CreateReporteModal';
import ConfirmDeleteModal from '../Presupuestos/ConfirmDeleteModal';

const ESTATUS_OPTIONS = ['PENDIENTE', 'EN PROCESO', 'EJECUTADO'];

const ReportesTab = () => {
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [imagenPreview, setImagenPreview] = useState(null);
  const [reporteAEliminar, setReporteAEliminar] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/equipos-moviles/reportes');
      setReportes(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  const handleEstatusChange = async (id, value) => {
    try {
      await axiosInstance.put(`/api/equipos-moviles/reportes/${id}`, { estatus: value });
      setReportes(prev => prev.map(r => r.id === id ? { ...r, estatus: value } : r));
      toast.success('Estatus actualizado');
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Error al actualizar');
      return false;
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/api/equipos-moviles/reportes/${id}`);
      setReportes(prev => prev.filter(r => r.id !== id));
      toast.success('Reporte eliminado');
    } catch (error) {
      console.error(error);
      toast.error('Error al eliminar');
    } finally {
      setReporteAEliminar(null);
    }
  };

  const columns = useMemo(() => [
    {
      header: 'Fecha',
      accessorKey: 'fecha',
      size: 100,
      cell: ({ row }) => <span className="text-sm text-gray-600 font-mono">{row.original.fecha}</span>
    },
    {
      header: 'Equipo',
      accessorKey: 'equipo',
      cell: ({ row }) => <div className="font-semibold text-gray-800 text-sm">{row.original.equipo}</div>
    },
    {
      header: 'Tipo',
      accessorKey: 'tipo_mtto',
      size: 110,
      cell: ({ row }) => <span className="text-xs font-medium text-gray-500">{row.original.tipo_mtto}</span>
    },
    {
      header: 'Parte / Falla',
      accessorKey: 'parte_falla',
      cell: ({ row }) => <span className="text-sm text-gray-700">{row.original.parte_falla || '—'}</span>
    },
    {
      header: 'Detalle',
      accessorKey: 'detalle',
      cell: ({ row }) => <span className="text-xs text-gray-500 line-clamp-2">{row.original.detalle || '—'}</span>
    },
    {
      header: 'Realizado Por',
      accessorKey: 'realizado_por',
      size: 140,
      cell: ({ row }) => <span className="text-xs text-gray-500">{row.original.realizado_por || '—'}</span>
    },
    {
      header: 'Imagen',
      id: 'imagen',
      size: 70,
      cell: ({ row }) => row.original.image_url ? (
        <button onClick={() => setImagenPreview(row.original.image_url)} className="text-blue-600 hover:text-blue-800">
          <ImageIcon size={16} />
        </button>
      ) : <span className="text-gray-300 text-xs">—</span>
    },
    {
      header: 'Estatus',
      accessorKey: 'estatus',
      size: 130,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.estatus}
          options={ESTATUS_OPTIONS}
          onSave={(val) => handleEstatusChange(row.original.id, val)}
        />
      )
    },
    {
      id: 'acciones',
      header: '',
      size: 50,
      cell: ({ row }) => (
        <button
          onClick={() => setReporteAEliminar(row.original.id)}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          title="Eliminar reporte"
        >
          <Trash2 size={14} />
        </button>
      )
    }
  ], []);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all shadow-sm bg-sura-blue hover:bg-sura-blue-dark"
        >
          <Plus size={14} /> Nuevo Reporte
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sura-blue"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <TableView
            data={reportes}
            columns={columns}
          />
        </div>
      )}

      <CreateReporteModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchData}
      />

      <ConfirmDeleteModal
        isOpen={reporteAEliminar !== null}
        onClose={() => setReporteAEliminar(null)}
        onConfirm={() => handleDelete(reporteAEliminar)}
        title="Eliminar reporte"
        message="¿Seguro que deseas eliminar este reporte?"
      />

      {imagenPreview && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000] p-6"
          onClick={() => setImagenPreview(null)}
        >
          <img src={imagenPreview} alt="Reporte" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
};

export default ReportesTab;
